/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';

import {
  ApiResponse,
  BaseComponent,
  ClosedPaymentService,
  Column,
  CompoApiService,
  ConfirmDialogComponent,
  CrmIssue,
  CrmJob,
  DateService,
  FileService,
  GroupedIssueActivityLog,
  IdAndHeader,
  IssueEnvironment,
  IssueType,
  Picture,
  StoreService,
  Tag,
  Term
} from 'compob2b-libs';
import {FormControl, Validators} from '@angular/forms';
import {
  delay,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  shareReplay,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';
import {interval, Observable, Subject} from 'rxjs';
import {NbDialogRef, NbGlobalPhysicalPosition, NbMenuItem, NbMenuService, NbTabComponent} from '@nebular/theme';
import {translations} from '../../../../../../environments/translations.ru';
import {JobModalComponent} from '../../../../dashboard/job-modal/job-modal.component';
import {NgxDurationInputService} from '../../../../../admin-shared/services/ngx-duration-input.service';
import {MoveToProjectModalComponent} from '../../../../dashboard/move-to-project-modal/move-to-project-modal.component';
import {Columns, Config, DefaultConfig} from 'ngx-easy-table';
import {AuthService} from 'compob2b-auth';
import {Clipboard} from '@angular/cdk/clipboard';
import {ActivatedRoute, Router} from '@angular/router';
import {ImagePreviewComponent} from '../../../../../admin-shared/image-preview/image-preview.component';
import {NbAccessChecker} from "@nebular/security";
import {DatePipe} from '@angular/common';

export interface IssueParent {
  id: number;
  parent: number | null;
}

@Component({
    selector: 'app-job-modal',
    templateUrl: './issue-modal.component.html',
    styleUrls: ['./issue-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class IssueModalComponent extends BaseComponent implements OnInit, AfterViewInit, AfterViewChecked {
  onUpdate = new EventEmitter<boolean>();
  loading = false;
  date: Date;
  issueId: number;
  bkOpen = false;
  setTitle = false;
  columns: Column[];
  issue: CrmIssue;
  terms$: Observable<Term[]>;
  merges$: Observable<any>;
  showForm = false;
  imageOpenDone = false;
  isHeaderInput = false;
  contentControl = new FormControl('', Validators.required);
  selectedColumn: number;
  showWebLinkForm = false;
  showTaskLinkForm = false;
  showSubTaskLinkForm = false;
  showIssueChecklist = false;
  actions: NbMenuItem[] = [
    {title: 'Редактировать', data: 'edit', icon: 'edit-outline'},
    {title: 'Логировать время', data: 'logTime', icon: 'clock-outline'},
    {title: 'Копировать название', data: 'copy-name', icon: 'copy'},
    {title: 'Копировать ссылку', data: 'copy-link', icon: 'copy'},
    {title: 'В другой проект', data: 'move-to-project', icon: 'arrowhead-right-outline'},
    {title: 'Архивировать', data: 'archive', icon: 'archive-outline'},
  ];

  @ViewChild('assigneeTpl', {static: true}) assigneeTpl: TemplateRef<any>;
  @ViewChild('startTime', {static: true}) startTime: TemplateRef<any>;
  @ViewChild('spent', {static: true}) spent: TemplateRef<any>;
  @ViewChild(ImagePreviewComponent) preview: ImagePreviewComponent;

  uploading = false;
  uploader: any;

  imgSelector = '.issue-card tui-editor-socket img';

  jobs$: Observable<CrmJob[]>;
  jobsConfiguration: Config;
  jobColumns: Columns[];

  linkActions: NbMenuItem[] = [
    {title: 'Ссылка на задачу', data: 'taskLink', icon: 'checkmark-square-2-outline'},
    {title: 'Ссылка на веб-страницу', data: 'httpLink', icon: 'globe-2-outline'}
  ];

  private readonly contextMenu$ = this.nbMenuService.onItemClick().pipe(filter(({tag}) => tag === 'context-menu'));
  private readonly linkContextMenu$ = this.nbMenuService.onItemClick().pipe(filter(({tag}) => tag === 'link-context-menu'));
  public readonly translations = translations;

  private taskLinks: ElementRef;
  private webLinks: ElementRef;
  private issueCheckList: ElementRef;
  private subTaskLinks: ElementRef;
  private saveChangedHeaderEvent$: Subject<string> = new Subject();
  allLogs: GroupedIssueActivityLog[] = [];

  @ViewChild('uploader', {static: false}) set uploaderContent(content: ElementRef) {
    if (content) {
      this.uploader = content;
    }
  }

  @ViewChild('webLinks', {static: false}) set content(content: ElementRef) {
    if (content) {
      this.webLinks = content;
    }
  }

  @ViewChild('taskLinks', {static: false}) set taskContent(content: ElementRef) {
    if (content) {
      this.taskLinks = content;
    }
  }

  @ViewChild('subTaskLinks', {static: false}) set subTaskContent(content: ElementRef) {
    if (content) {
      this.subTaskLinks = content;
    }
  }

  @ViewChild('issueCheckList', {static: false}) set issueCheckListContent(content: ElementRef) {
    if (content) {
      this.issueCheckList = content;
    }
  }

  constructor(private readonly apiService: CompoApiService,
              private readonly fileService: FileService,
              private readonly dialogRef: NbDialogRef<any>,
              private nbMenuService: NbMenuService,
              private readonly durationService: NgxDurationInputService,
              private dateService: DateService,
              private clipboard: Clipboard,
              private readonly authService: AuthService,
              private readonly closedPaymentService: ClosedPaymentService,
              private readonly storeService: StoreService,
              private readonly route: ActivatedRoute,
              private readonly router: Router,
              protected readonly accessChecker: NbAccessChecker,
              private readonly datePipe: DatePipe,
  ) {
    super();
  }

  isClientRole(): boolean {
    return this.authService.isClient();
  }

  isClientCamozzi(): boolean {
    return this.authService.isClientCamozzi();
  }

  ngOnInit(): void {
    this.loading = true;
    this.jobsConfiguration = {...DefaultConfig};
    this.jobsConfiguration.paginationEnabled = true;
    this.jobsConfiguration.isLoading = true;

    this.jobsConfiguration.paginationEnabled = false;
    this.jobsConfiguration.serverPagination = false;
    this.jobsConfiguration.paginationRangeEnabled = false;
    this.jobsConfiguration.rows = 10000;

    this.loadIssue().pipe(takeUntil(this.unsubscribe)).subscribe(issue => this.handleResponse(issue, true));
    this.loadColumns().pipe(takeUntil(this.unsubscribe)).subscribe(columns => this.columns = columns);

    this.terms$ = this.apiService.get<Term[]>('/issue/' + this.issueId + '/terms')
      .pipe(map(response => response.data));

    this.merges$ = this.apiService.get<Term[]>('/issue/' + this.issueId + '/merges')
      .pipe(
        map(response => response.data),
        shareReplay(1),
      );

    this.contextMenu$.pipe(
      map(({item: {data}}) => data),
      tap(action => this.contextMenuAction(action)),
      takeUntil(this.unsubscribe)
    ).subscribe();

    this.linkContextMenu$.pipe(
      map(({item: {data}}) => data),
      tap(action => this.linkContextMenuAction(action)),
      takeUntil(this.unsubscribe)
    ).subscribe();

    this.dialogRef.onClose.pipe(
      take(1)
    ).subscribe(_ => {
      this.preview?.close();
    });

    this.accessChecker.isGranted('view', 'issue-activity-log')
      .pipe(
        distinctUntilChanged(),
        filter(isGranted => isGranted === true),
        switchMap(_ => this.getLogs()),
        takeUntil(this.unsubscribe),
      )
      .subscribe(logs => {
        this.allLogs = logs;
        this.cdr.detectChanges();
      });

    this.saveChangedHeaderEvent$
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.unsubscribe),
      )
      .subscribe(newHeader => this.doUpdateAndHandle({header: newHeader}));

    this.onUpdate
      .pipe(
        delay(400),
        switchMap(_ => this.getLogs()),
        takeUntil(this.unsubscribe),
      )
      .subscribe(logs => {
        this.allLogs = logs;
        this.cdr.detectChanges();
      });
  }

  private getLogs(): Observable<GroupedIssueActivityLog[]> {
    return this.apiService.get<any[]>(`/issue/${this.issueId}/activity-logs`)
      .pipe(
        map(response => response.data),
      );
  }

  ngAfterViewInit() {
    this.jobColumns = [
      {
        key: 'assignee',
        title: 'Исполнитель',
        cellTemplate: this.assigneeTpl
      },
      {
        key: 'startAt',
        title: translations.startTime,
        cellTemplate: this.startTime
      },
      {
        key: 'spent',
        title: translations.spent,
        cellTemplate: this.spent
      },
    ];
  }

  getShiftedTimeForOutput(time: string): string {
    return this.dateService.getShiftedTimeForOutput(new Date(time)).toString();
  }

  private contextMenuAction(action: string) {
    switch (action) {
      case 'logTime':
        this.addJob();
        break;
      case 'archive':
        this.archive();
        break;
      case 'move-to-project':
        this.moveToProject();
        break;
      case 'edit':
        this.edit();
        break;
      case 'copy-name':
        this.copyName();
        break;
      case 'copy-link':
        this.copyLink();
        break;
    }
  }

  private linkContextMenuAction(action: string) {
    switch (action) {
      case 'taskLink':
        this.showTaskLinks();
        break;
      case 'httpLink':
        this.showHttpLinks();
        break;
    }
  }

  private showHttpLinks(): void {
    if (this.showWebLinkForm) {
      this.showWebLinkForm = false;
      this.cdr.detectChanges();
    }
    this.showWebLinkForm = true;
    this.cdr.detectChanges();
    this.scroll(this.webLinks.nativeElement);
  }

  private showTaskLinks(): void {
    if (this.showTaskLinkForm) {
      this.showTaskLinkForm = false;
      this.cdr.detectChanges();
    }
    this.showTaskLinkForm = true;
    this.cdr.detectChanges();
    this.scroll(this.taskLinks.nativeElement);
  }

  private handleResponse(response: CrmIssue, skipUpdate?: boolean) {
    this.issue = response;

    if (!skipUpdate) {
      this.storeService.updateIssue(response.id, response);
    }

    if (this.setTitle) {
      this.storeService.setTitle(this.issue.header);
    }
    if (response.crmColumn === null) {
      this.selectedColumn = null;
    } else {
      this.selectedColumn = response.crmColumn.id;
    }

    this.contentControl.patchValue(response.content);

    this.loading = false;
    this.cdr.detectChanges();
  }

  get issueControl(): FormControl {
    return this.contentControl as FormControl;
  }

  getImageUri(picture: Picture): string {
    return picture === null ? null : this.fileService.getImageUri(picture, 96);
  }

  save() {
    this.doUpdateAndHandle({content: this.issueControl.value});
  }

  saveHeader() {
    this.saveChangedHeaderEvent$.next(this.issue.header);
  }

  getBkLink(): any[] {
    if (this.bkOpen) {
      return ['/cabinet/crm/project/show/' + this.issue.crmProject.id];
    } else {
      return ['/cabinet/crm/project/show/', this.issue.crmProject.id, 'edit-issue', this.issue.iid];
    }
  }

  getBkParams(): any {
    if (this.bkOpen) {
      return {issueId: this.issueId};
    } else {
      return {};
    }
  }

  showHeaderInput() {
    this.isHeaderInput = true; // !IssueModalComponent.isSelectionText();
    this.showForm = false;
  }

  formatDuration(n: number) {
    return this.durationService.getDurationString(n);
  }

  close() {
    this.dialogRef.close();
  }

  addJob() {
    this.dialogService.open(JobModalComponent, {
      context: {
        date: new Date(),
        issue: this.issue,
        canRemoveTask: false
      }
    });
  }

  moveToProject() {
    this.dialogService.open(MoveToProjectModalComponent, {
      context: {
        issueId: this.issueId,
        issueName: this.issue.header
      }
    });
  }

  edit(): void {
    window.location.href = '/cabinet/crm/project/show/' + this.issue.crmProject.id + '/edit-issue/' + this.issue.iid;
  }

  copyName(): void {
    const name = '[' + this.issue.crmProject.alias + '-' + this.issue.iid + '] ' + this.issue.header;

    this.clipboard.copy(name);
    this.toastrService.show(null, 'Название задачи скопировано в буфер обмена', {
      icon: null,
      status: 'success',
    });
  }

  copyLink(): void {
    const link = window.location.protocol + '//' + window.location.host + '/task/' + this.issue.crmProject.alias + '-' + this.issue.iid;

    this.clipboard.copy(link);
    this.toastrService.show(null, 'Ссылка на задачу скопирована в буфер обмена', {
      icon: null,
      status: 'success',
    });
  }

  get isEpic(): boolean {
    return this.issue.crmIssueType.id === Number(IssueType.EPIC);
  }

  get isSubTask(): boolean {
    return this.issue.crmIssueType.id === Number(IssueType.SUBTASK);
  }

  get canSubTask(): boolean {
    return !this.isEpic && !this.isSubTask;
  }

  changeColumn(columnId: number) {
    const column = this.columns.find(item => item.id == columnId as unknown as number);
    if (column !== undefined && column.columnType > 2 && this.issue.estimate === 0 && !this.authService.isClient()) {
      this.toastrService.show(null, 'Оцените задачу перед сменой статуса', {
        status: 'warning',
        icon: null,
        position: NbGlobalPhysicalPosition.TOP_RIGHT
      });
      return;
    }

    if (this.issue.crmColumn === null) {
      this.issue.crmColumn = {
        id: 0
      };
    }
    if (columnId !== this.issue.crmColumn.id) {
      this.issue.crmColumn.id = columnId;
      this.loading = true;
      this.apiService
        .post('/columns/save-issues-and-columns-pos', {
          newSortContainer: [],
          newSortContainerId: columnId,
          newSortPreviousContainer: [],
          newSortPreviousContainerId: 0,
          issueId: this.issue.id,
        }).pipe(
        tap(_ => this.handleChangeColumnResponse(this.issue)),
        takeUntil(this.unsubscribe)
      ).subscribe();
    }
  }

  private handleChangeColumnResponse(issue: CrmIssue) {
    this.storeService.updateIssue(issue.id, issue);
    this.loading = false;
    this.onUpdate.emit(true);
    this.cdr.detectChanges();
  }

  private archive() {
    this.dialogService.open(ConfirmDialogComponent, {
      context: {
        description: translations.confirmIssueArchiveTemplate
      },
    }).onClose.pipe(
      filter(result => result === true), takeUntil(this.unsubscribe))
      .subscribe(_ => {
        this.doUpdateAndHandle({enabled: false});
      });
  }

  private handleEmptyResponse(response: CrmIssue) {
    this.issue = response;
    this.loading = false;
    this.cdr.detectChanges();
    this.onUpdate.emit(true);
  }

  private scroll(el: HTMLElement) {
    el.scrollIntoView({behavior: 'smooth'});
  }

  openSubTaskLinkForm() {
    if (this.showSubTaskLinkForm) {
      this.showSubTaskLinkForm = false;
      this.cdr.detectChanges();
    }
    this.showSubTaskLinkForm = true;
    this.cdr.detectChanges();
    this.scroll(this.subTaskLinks.nativeElement);
  }

  private loadColumns(): Observable<Column[]> {
    const url = '/issue/' + this.issueId + '/columns';

    return this.apiService.get<Column[]>(url).pipe(
      map(response => response.data)
    );
  }

  private loadIssue(): Observable<CrmIssue> {
    return this.apiService.get<CrmIssue>('/issue/' + this.issueId).pipe(
      map(response => response.data)
    );
  }

  onChangeEstimate(value: number) {
    this.doUpdateAndHandle({estimate: value});
  }

  onChangeEstimateForClient(value: number) {
    this.doUpdateAndHandle({estimateForClient: value});
  }

  onChangeDueDate(value: Date) {
    this.doUpdateAndHandle({dueDate: value});
  }

  onChangeAssignee(value: IdAndHeader) {
    if (typeof value === 'object') {
      this.doUpdateAndHandle({assigneeId: value.id});
    }
  }

  onChangeRelatedPersons(value: IdAndHeader[]) {
    if (typeof value === 'object') {
      this.doUpdateAndHandle({relatedPersons: value.map(item => item.id)});
    }
  }

  onChangePayment(value: IdAndHeader) {
    if (value === undefined) {
      this.doUpdateAndHandle({subprojectId: 0});
    } else if (typeof value === 'object') {
      this.doUpdateAndHandle({subprojectId: value.id});
    }
  }

  onChangeEpicName(value: string) {
    this.doUpdateAndHandle({epicName: value});
  }

  onChangeTag(tag: Tag) {
    this.doUpdateAndHandle({tagId: tag.id});
  }

  onChangeEnvironment(environmentList: IssueEnvironment[]) {
    this.doUpdateAndHandle({environmentIdList: environmentList.map(item => item.id)});
  }

  private doUpdateAndHandle(obj): void {
    this.loading = true;
    this.isHeaderInput = false;
    this.showForm = false;

    const issue = {
      id: this.issueId,
    };

    for (const [key, value] of Object.entries(obj)) {
      issue[key] = value;
    }

    this.apiService
        .patch('/issue/' + this.issueId, issue)
        .pipe(
            map(response => response.data),
            takeUntil(this.unsubscribe),
            finalize(() => {
              this.loading = false;
              this.cdr.detectChanges();
            })
        )
        .subscribe({
          next: (response) => {
            if (response) {
              this.handleResponse(response as CrmIssue);
              this.onUpdate.emit(true);
            }
          },
          error: (error) => {
            console.error('Error updating issue:', error);
          }
        });
  }

  onUpdateParent($event: number | null): void {
    this.apiService.patch<IssueParent | null, CrmIssue>('/issue/' + this.issueId + '/parent', {
      id: this.issueId,
      parent: $event
    }).pipe(
      map((response: ApiResponse<CrmIssue>) => response.data),
      tap((response: CrmIssue) => this.handleEmptyResponse(response)),
      takeUntil(this.unsubscribe)
    ).subscribe();
  }

  openIssueCheckbox() {
    this.showIssueChecklist = true;
    this.cdr.detectChanges();
    this.scroll(this.issueCheckList.nativeElement);
  }

  loadJobs(event: NbTabComponent) {
    if (event.tabId === 'Jobs') {

      this.jobs$ = this.apiService.get<CrmJob[]>('/issue/' + this.issueId + '/jobs').pipe(
        map(response => {
          this.jobsConfiguration.isLoading = false;
          return response.data;
        }),
        takeUntil(this.unsubscribe)
      );
    }
  }

  canChangePayment(): boolean {
    return this.authService.isSuperAdmin() || !this.closedPaymentService.isIssuePaymentClosed(this.issue);
  }

  @HostListener('document:keydown.escape')
  onKeydownHandler() {
    if (!document.querySelectorAll('tui-editor, .t-dialog').length) {
      this.dialogRef.close();
    }
  }

  click(event) {
    if (event.target?.tagName == 'IMG' && event.target.closest('tui-editor-socket')) {
      this.openPreview(event.target.getAttribute('src'));
    }
  }

  openImage(index: number) {
    const image = document.querySelectorAll(this.imgSelector)[index] as HTMLImageElement;
    if (image) {
      this.openPreview(image.src);
    }
  }

  openPreview(src: string) {
    this.preview.show(src, this.imgSelector, !this.bkOpen);
  }

  ngAfterViewChecked() {
    interval(500).pipe(takeUntil(this.unsubscribe)).subscribe(_ => {
        if (this.imageOpenDone || !this.route) {
          return;
        }

        const images = document.querySelectorAll(this.imgSelector);
        if (images.length) {
          const img = this.route.snapshot.queryParams.img;
          if (img == undefined) {
            this.imageOpenDone = true;
            return;
          }

          if (images[img]) {
            this.imageOpenDone = true;
            this.openImage(img);
          }
        }
      }
    );
  }
}
